// src/App.tsx with improved auth handling
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import Navbar from './components/common/Navbar';
import Sidebar from './components/common/Sidebar';
import Dashboard from './pages/Dashboard';
import ArtistDetail from './pages/ArtistDetail';
import SongDetail from './components/songs/SongDetail';
import AddMetrics from './pages/AddMetrics';
import Comparisons from './pages/Comparisons';
import AuthCallback from './pages/AuthCallback';
import UserProfile from './pages/UserProfile';
import LoginPage from './pages/LoginPage';
import AuthNotification from './components/common/AuthNotification';
import AuthPrompt from './components/auth/AuthPrompt';
import { initAuth, isAuthenticated, checkAuthStatus } from './services/authService';
import LoadingState from './components/common/LoadingState';

// Define which routes require authentication
const protectedRoutes = [
  '/',  // Dashboard is now protected
  '/add-metrics',
  '/profile',
  '/settings'
];

// Check if the current path is a protected route
const isProtectedRoute = (path: string): boolean => {
  // Check standard protected routes
  if (protectedRoutes.some(route => path === route || path.startsWith(`${route}/`))) {
    return true;
  }
  
  // Special case for /artist/me and its sub-routes
  if (path === '/artist/me' || path.startsWith('/artist/me/')) {
    return true;
  }
  
  // Handle dynamic routes that include 'me' parameter
  const artistMePattern = /^\/artist\/me(?:\/|$)/;
  if (artistMePattern.test(path)) {
    return true;
  }
  
  return false;
};

function App() {
  const [initializing, setInitializing] = useState(true);
  
  useEffect(() => {
    const initialize = async () => {
      // Initialize auth when app starts
      await initAuth();
      setInitializing(false);
    };
    
    initialize();
  }, []);
  
  // Show loading screen during initialization
  if (initializing) {
    return <LoadingState message="Loading application..." size="large" />;
  }
  
  return (
    <Router>
      <Routes>
        {/* Auth callback route - doesn't include main layout */}
        <Route path="/callback" element={<AuthCallback />} />
        
        {/* All other routes, including login, use the main layout */}
        <Route path="*" element={<AppLayout />} />
      </Routes>
    </Router>
  );
}

// ProtectedRoute wrapper component
const ProtectedRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const location = useLocation();
  const authenticated = isAuthenticated();
  
  if (!authenticated) {
    // Redirect to login if not authenticated
    return (
      <div className="container mx-auto py-8">
        <AuthPrompt />
      </div>
    );
  }
  
  return element;
};

// Component for main app layout with Navbar and Sidebar
const AppLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(false);
  
  // Check if the current route is protected and redirect if needed
  useEffect(() => {
    if (!authReady) return;
    
    const path = location.pathname;
    
    // Explicit check for Dashboard (root path)
    if (path === '/' && !isAuthenticated()) {
      console.log('Dashboard requires authentication, redirecting to login');
      navigate('/login', { state: { returnPath: path } });
      return;
    }
    
    // Get route parameters for artist routes
    const params = location.pathname.split('/');
    const isArtistRoute = params[1] === 'artist';
    const artistId = params[2];
    
    // Explicit check for /artist/me route
    if (isArtistRoute && artistId === 'me' && !isAuthenticated()) {
      console.log('Artist/me route requires authentication, redirecting to login');
      navigate('/login', { state: { returnPath: location.pathname } });
      return;
    }
    
    // Check other protected routes
    if (isProtectedRoute(location.pathname) && !isAuthenticated()) {
      // For all protected routes: immediately redirect to login page
      console.log(`Protected route ${location.pathname} requires authentication, redirecting to login`);
      navigate('/login', { state: { returnPath: location.pathname } });
    }
  }, [location.pathname, authReady, navigate]);
  
  useEffect(() => {
    // Ensure authentication status is current
    checkAndUpdateStatus();

    // Check auth status regularly
    const intervalId = setInterval(checkAndUpdateStatus, 60000); // Every 60 seconds

    // Listen for auth-change events
    window.addEventListener('auth-change', checkAndUpdateStatus);
    
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('auth-change', checkAndUpdateStatus);
    };
  }, []);

  const checkAndUpdateStatus = () => {
    // Check detailed auth status
    const status = checkAuthStatus();
    const isLoggedIn = status.isAuthenticated && !status.needsRenewal;
    
    // Check current route
    const currentPath = location.pathname;
    
    // For protected routes: redirect to login if not authenticated
    if (isProtectedRoute(currentPath) && !isLoggedIn && currentPath !== '/login') {
      console.log("Protected route requires authentication, redirecting to login");
      navigate('/login', { state: { returnPath: currentPath } });
    }
    
    setAuthReady(true);
  };
  
  // Show empty layout during auth check
  if (!authReady) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Navbar />
          <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 p-4">
            <LoadingState message="Checking authentication..." />
          </main>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar />
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 p-4">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            
            {/* Login route now included in the main layout */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Protected routes */}
            <Route path="/add-metrics" element={<AddMetrics />} />
            <Route path="/profile" element={<UserProfile />} />
            
            {/* Special case for artist/:id route - only redirect if id is 'me' */}
            <Route path="/artist/:id" element={<ArtistDetail />} />
            <Route path="/artist/:artistId/song/:songId" element={<SongDetail />} />
            
            {/* Other routes */}
            <Route path="/comparisons" element={<Comparisons />} />
            <Route path="/favorites" element={<Comparisons />} />
            <Route path="/browse" element={<Comparisons />} />
          </Routes>
        </main>
        
        {/* Authentication notification */}
        <AuthNotification />
      </div>
    </div>
  );
};

export default App;